import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Box,
  Grid,
  Typography,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  InputAdornment,
  Divider,
} from '@mui/material';
import {
  Close as CloseIcon,
  AttachMoney as AttachMoneyIcon,
} from '@mui/icons-material';
import { GuestSpeaker } from '../../types';
import apiService from '../../services/api';
import { toast } from 'react-toastify';
import { Formik, Form, FieldArray } from 'formik';
import {
  speakerValidationSchema,
  trimObjectStrings,
} from '../../Yup/Speaker.yup';
import Swal from 'sweetalert2';
import { NumberInputWrapper } from '../../styles/globalStyle';
import { RequiredLabel } from '../common/FormTextField';

interface SpeakerEditDialogProps {
  open: boolean;
  speaker: GuestSpeaker | null;
  onClose: () => void;
  onSave: () => void;
}

const SpeakerEditDialog: React.FC<SpeakerEditDialogProps> = ({
  open,
  speaker,
  onClose,
  onSave,
}) => {
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (speaker?.profileImageUrl) {
      setPreviewImage(speaker.profileImageUrl);
    } else {
      setPreviewImage(null);
    }
  }, [speaker]);

  ////////////////// image upload //////////////////
  const handleUploadImage = async (
    e: React.ChangeEvent<HTMLInputElement>,
    setFieldValue: any
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const tempPreview = URL.createObjectURL(file);
    setPreviewImage(tempPreview);

    // reset file input so user can select same file again later
    e.target.value = '';

    const form = new FormData();
    form.append('file', file);
    setUploadLoading(true);
    setError('');

    try {
      const response = await apiService.uploadSpeakerImage(form);
      toast.success(response.message);

      const finalUrl = `${process.env.REACT_APP_IMAGE_URL}/${response.data.filename}`;
      setPreviewImage(finalUrl);
      setFieldValue('profileImageUrl', finalUrl);
    } catch (err: any) {
      setError('Image upload failed');
      setPreviewImage(null);
    } finally {
      setUploadLoading(false);
    }
  };

  //////////////delete image///////////////////
  const handleDeleteImage = async (setFieldValue: any, values: any) => {
    const profileImageUrl = values.profileImageUrl;
    if (!profileImageUrl) return;

    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'Do you really want to remove this image?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, Remove',
    });

    if (!result.isConfirmed) return;

    try {
      await apiService.deleteSpeakerImage(profileImageUrl);

      setFieldValue('profileImageUrl', '');
      setPreviewImage(null);
      toast.success('Image removed');
    } catch (err) {
      toast.error('Failed to delete image');
    }
  };

  const removeEmpty = (obj: any) => {
    const cleaned: any = {};
    Object.keys(obj).forEach(key => {
      const value = obj[key];

      // skip undefined, null, empty string, empty array
      if (
        value === undefined ||
        value === null ||
        value === '' ||
        (Array.isArray(value) && value.length === 0)
      ) {
        return;
      }

      cleaned[key] = value;
    });
    return cleaned;
  };

  const normalizeSpeaker = (speaker: GuestSpeaker | null) => {
    if (!speaker) return null;

    return {
      ...speaker,
      linkedinUrl: speaker.linkedinUrl ?? '',
      twitterUrl: speaker.twitterUrl ?? '',
      websiteUrl: speaker.websiteUrl ?? '',
    };
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            {speaker ? 'Edit Speaker' : 'Add Speaker'}
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <Formik
        enableReinitialize
        validateOnChange={true}
        validateOnBlur={false}
        initialValues={
          normalizeSpeaker(speaker) ?? {
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            designation: '',
            organization: '',
            bio: '',
            profileImageUrl: '',
            linkedinUrl: '',
            twitterUrl: '',
            websiteUrl: '',
            expertiseAreas: [],
            speakingTopics: [],
            yearsOfExperience: undefined,
            // speakerFee: undefined,
            isKeynoteSpeaker: false,
            isFeaturedSpeaker: false,
            travelRequired: false,
            accommodationRequired: false,
            specialRequirements: '',
            isActive: true,
          }
        }
        validationSchema={speakerValidationSchema}
        onSubmit={async (values, { setSubmitting }) => {
          if (!values.profileImageUrl) {
            setSubmitting(false);
            await Swal.fire({
              title: 'Image Required',
              text: 'Please upload a speaker photo before submitting.',
              icon: 'warning',
              confirmButtonText: 'OK',
              focusConfirm: false,
            });
            return;
          }

          try {
            // typeof values === 'string' ? values.trim() : values;
            const trimmedValues = trimObjectStrings(values);
            const payload = removeEmpty(trimmedValues);

            if (speaker) {
              await apiService.updateGuestSpeaker(speaker.id, payload);
              toast.success('Speaker updated successfully');
            } else {
              await apiService.createGuestSpeaker(payload);
              toast.success('Speaker created successfully');
            }

            onSave();
          } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to save speaker');
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          setFieldValue,
          isSubmitting,
          handleBlur,
        }) => (
          <Form>
            <DialogContent dividers>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {' '}
                  {error}{' '}
                </Alert>
              )}
              {/* Upload Image */}
              <Typography sx={{ fontWeight: 600, mb: 1 }}>
                {' '}
                Speaker Photo{' '}
              </Typography>
              <Box
                display="flex"
                gap={2}
                alignItems="center"
                sx={{
                  border: '1px solid #ddd',
                  p: 2,
                  mb: 2,
                  display: 'flex',
                  bgcolor: '#f9f9f9',
                }}
              >
                <Box
                  width={110}
                  height={110}
                  border="1px solid #ccc"
                  sx={{
                    overflow: 'hidden',
                    borderRadius: 2,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  {previewImage ? (
                    <img
                      src={previewImage}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                  ) : (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ textAlign: 'center', margin: 5 }}
                    >
                      No Image Selected
                    </Typography>
                  )}
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Button
                    variant="contained"
                    component="label"
                    disabled={uploadLoading}
                  >
                    {uploadLoading ? 'Uploading...' : 'Upload Photo'}
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={e => handleUploadImage(e, setFieldValue)}
                    />
                  </Button>

                  {previewImage && (
                    <Button
                      variant="contained"
                      component="label"
                      color="error"
                      onClick={() => handleDeleteImage(setFieldValue, values)}
                    >
                      Remove
                    </Button>
                  )}
                </Box>
              </Box>

              {/* Text Fields */}
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography sx={{ fontWeight: 600, mb: 1 }}>
                  Basic Information
                </Typography>
              </Grid>
              <Grid container spacing={3}>
                {/* first name */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={<RequiredLabel label="First Name" />}
                    name="firstName"
                    value={values.firstName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.firstName && Boolean(errors.firstName)}
                    helperText={touched.firstName && errors.firstName}
                  />
                </Grid>
                {/* last name */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={<RequiredLabel label="Last Name" />}
                    name="lastName"
                    value={values.lastName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.lastName && Boolean(errors.lastName)}
                    helperText={touched.lastName && errors.lastName}
                  />
                </Grid>
                {/* email */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={<RequiredLabel label="Email" />}
                    name="email"
                    value={values.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.email && Boolean(errors.email)}
                    helperText={touched.email && errors.email}
                  />
                </Grid>
                {/* phone */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={<RequiredLabel label="Phone" />}
                    name="phone"
                    value={values.phone}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.phone && Boolean(errors.phone)}
                    helperText={touched.phone && errors.phone}
                  />
                </Grid>
                {/* designation */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={<RequiredLabel label="Designation" />}
                    name="designation"
                    value={values.designation}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.designation && Boolean(errors.designation)}
                    helperText={touched.designation && errors.designation}
                  />
                </Grid>
                {/* organization */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={<RequiredLabel label="Organization" />}
                    name="organization"
                    value={values.organization}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.organization && Boolean(errors.organization)}
                    helperText={touched.organization && errors.organization}
                  />
                </Grid>

                {/* Bio */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label={<RequiredLabel label="Bio" />}
                    name="bio"
                    value={values.bio}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.bio && Boolean(errors.bio)}
                    helperText={touched.bio && errors.bio}
                  />
                </Grid>

                {/* Social */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography sx={{ fontWeight: 600, mb: 1 }}>
                    Social Links
                  </Typography>
                </Grid>
                {/* Linkdin url*/}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Linkedin URL"
                    name="linkedinUrl"
                    value={values.linkedinUrl}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.linkedinUrl && Boolean(errors.linkedinUrl)}
                    helperText={touched.linkedinUrl && errors.linkedinUrl}
                  />
                </Grid>
                {/* Twitter url */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Twitter URL"
                    name="twitterUrl"
                    value={values.twitterUrl}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.twitterUrl && Boolean(errors.twitterUrl)}
                    helperText={touched.twitterUrl && errors.twitterUrl}
                  />
                </Grid>
                {/* Website url */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Website URL"
                    name="websiteUrl"
                    value={values.websiteUrl}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.websiteUrl && Boolean(errors.websiteUrl)}
                    helperText={touched.websiteUrl && errors.websiteUrl}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography sx={{ fontWeight: 600, mb: 1 }}>
                    Professional Details
                  </Typography>
                </Grid>
                {/* Experience + Fee */}
                <Grid item xs={12} sm={6}>
                  <NumberInputWrapper>
                    <TextField
                      label={<RequiredLabel label="Years of Experience" />}
                      type="number"
                      name="yearsOfExperience"
                      value={values.yearsOfExperience}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      fullWidth
                      error={
                        touched.yearsOfExperience &&
                        Boolean(errors.yearsOfExperience)
                      }
                      helperText={
                        touched.yearsOfExperience && errors.yearsOfExperience
                      }
                    />
                  </NumberInputWrapper>
                </Grid>
                {/* speaker fee */}
                {/* <Grid item xs={12} sm={6}>
                  <NumberInputWrapper>
                    <TextField
                      label="Speaker Fee"
                      type="number"
                      name="speakerFee"
                      value={values.speakerFee}
                      onChange={handleChange}
                      fullWidth
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <AttachMoneyIcon />
                          </InputAdornment>
                        ),
                      }}
                      error={touched.speakerFee && Boolean(errors.speakerFee)}
                      helperText={touched.speakerFee && errors.speakerFee}
                    />
                  </NumberInputWrapper>
                </Grid> */}

                {/* Expertise */}
                <Grid item xs={12}>
                  <FieldArray
                    name="expertiseAreas"
                    render={arrayHelpers => (
                      <>
                        <Typography fontWeight={600}>
                          Expertise Areas
                        </Typography>
                        <Box display="flex" gap={1} mt={1} mb={1}>
                          <TextField
                            fullWidth
                            placeholder="Add expertise"
                            onKeyDown={(e: any) => {
                              if (e.key === 'Enter' && e.target.value.trim()) {
                                e.preventDefault();
                                arrayHelpers.push(e.target.value.trim());
                                e.target.value = '';
                              }
                            }}
                          />
                        </Box>

                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {(values.expertiseAreas ?? []).map((tag, index) => (
                            <Chip
                              key={index}
                              label={tag}
                              onDelete={() => arrayHelpers.remove(index)}
                            />
                          ))}
                        </Box>

                        {/* {touched.expertiseAreas && errors.expertiseAreas && (
                          <Typography color="error" sx={{ mt: 1 }}>{errors.expertiseAreas as string}</Typography>
                        )} */}
                      </>
                    )}
                  />
                </Grid>

                {/* Speaking Topics */}
                <Grid item xs={12}>
                  <FieldArray
                    name="speakingTopics"
                    render={arrayHelpers => (
                      <>
                        <Typography fontWeight={600}>
                          Speaking Topics
                        </Typography>
                        <Box display="flex" gap={1} mt={1} mb={1}>
                          <TextField
                            fullWidth
                            placeholder="Add speaking topic"
                            onKeyDown={(e: any) => {
                              if (e.key === 'Enter' && e.target.value.trim()) {
                                e.preventDefault();
                                arrayHelpers.push(e.target.value.trim());
                                e.target.value = '';
                              }
                            }}
                          />
                        </Box>

                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {(values.speakingTopics ?? []).map((tag, index) => (
                            <Chip
                              key={index}
                              label={tag}
                              onDelete={() => arrayHelpers.remove(index)}
                            />
                          ))}
                        </Box>

                        {/* {touched.speakingTopics && errors.speakingTopics && (
                          <Typography color="error" sx={{ mt: 1 }}>{errors.speakingTopics as string}</Typography>
                        )} */}
                      </>
                    )}
                  />
                </Grid>

                {/* Switches */}
                {[
                  { key: 'isKeynoteSpeaker', label: 'Keynote Speaker' },
                  { key: 'isFeaturedSpeaker', label: 'Featured Speaker' },
                  { key: 'travelRequired', label: 'Travel Required' },
                  {
                    key: 'accommodationRequired',
                    label: 'Accommodation Required',
                  },
                  { key: 'isActive', label: 'Active' },
                ].map(({ key, label }) => (
                  <Grid item xs={12} sm={6} key={key}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={(values as any)[key]}
                          onChange={() =>
                            setFieldValue(key, !(values as any)[key])
                          }
                        />
                      }
                      label={label}
                    />
                  </Grid>
                ))}

                {/* Special Requirements */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="Special Requirements"
                    name="specialRequirements"
                    value={values.specialRequirements}
                    onChange={handleChange}
                  />
                </Grid>
              </Grid>
            </DialogContent>

            {/* <DialogActions>
              <Button onClick={onClose}>Cancel</Button>
              <Button type="submit" variant="contained" disabled={isSubmitting}>
                {isSubmitting ? (
                  <CircularProgress size={18} />
                ) : speaker ? (
                  'Update'
                ) : (
                  'Create'
                )}
              </Button>
            </DialogActions> */}

            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button
                onClick={onClose}
                // disabled={loading}
                variant="outlined"
                color="inherit"
                sx={{
                  backgroundColor: 'rgba(211, 47, 47, 0.08)',
                  color: '#d32f2f',
                  fontWeight: 500,
                  '&:hover': {
                    backgroundColor: 'rgba(211, 47, 47, 0.15)',
                  },
                }}
              >
                Cancel
              </Button>

              <Button type="submit" variant="contained" disabled={isSubmitting}>
                {isSubmitting ? (
                  <CircularProgress size={18} />
                ) : speaker ? (
                  'Update'
                ) : (
                  'Create'
                )}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default SpeakerEditDialog;
